import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiGift } from 'react-icons/fi';
import { getCommercialGesture, updateCommercialGesture } from '../../services/api';
import {
    getCgErrorToast,
    getCgFieldErrors,
    parseCgAmount,
    parseCgError,
    unwrapCgData,
} from './commercialGestures/commercialGestureUtils';
import {
    CgAmountField,
    CgPolicyPreview,
    CgStatusField,
    CgToastStack,
    useCgEditForm,
    useCgToasts,
    useCommercialGesturePermissions,
} from './commercialGestures/CommercialGestureShared';
import './commercialGestures/commercialGestures.css';

const AdminCommercialGestureEdit = () => {
    const { gestureId } = useParams();
    const navigate = useNavigate();
    const { canWrite } = useCommercialGesturePermissions();
    const { toasts, push, dismiss } = useCgToasts();
    const [gesture, setGesture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const form = useCgEditForm(gesture);

    useEffect(() => {
        if (!canWrite || !gestureId) return undefined;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = unwrapCgData(await getCommercialGesture(gestureId));
                if (!cancelled) setGesture(data);
            } catch (err) {
                if (!cancelled) setError(getCgErrorToast(parseCgError(err)));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [gestureId, canWrite]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.hasChanges) return;
        setFieldErrors({});

        const payload = {};
        if (form.amountChanged) {
            const parsed = parseCgAmount(form.amount);
            if (parsed.error) {
                setFieldErrors({ amount: parsed.error });
                return;
            }
            payload.amount = parsed.value;
        }
        if (form.statusChanged) payload.status = form.status;

        setSubmitting(true);
        try {
            const updated = unwrapCgData(await updateCommercialGesture(gestureId, payload));
            navigate(`/admin/trips/commercial-gestures/${updated?.id || gestureId}`, {
                state: { toast: { message: 'Geste commercial mis à jour.', variant: 'success' } },
            });
        } catch (err) {
            const parsed = parseCgError(err);
            if (parsed.status === 422) setFieldErrors(getCgFieldErrors(parsed));
            push(getCgErrorToast(parsed), 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    if (!canWrite) {
        return (
            <div className="dashboard-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <CgToastStack toasts={toasts} onDismiss={dismiss} />
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiGift className="me-2" aria-hidden="true" />
                                Modifier le geste commercial
                            </h2>
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate(`/admin/trips/commercial-gestures/${gestureId}`)}
                        >
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    {loading && <div className="text-center py-5"><Spinner animation="border" /></div>}
                    {error && !loading && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    {gesture && (
                        <Form className="mt-4" onSubmit={handleSubmit} noValidate style={{ maxWidth: 640 }}>
                            <CgPolicyPreview policy={gesture} />
                            <CgAmountField
                                id="cg-edit-amount"
                                value={form.amount}
                                onChange={form.setAmount}
                                error={fieldErrors.amount}
                                disabled={submitting}
                            />
                            <CgStatusField
                                id="cg-edit-status"
                                value={form.status}
                                onChange={form.setStatus}
                                error={fieldErrors.status}
                                disabled={submitting}
                            />
                            <Button type="submit" variant="primary" disabled={submitting || !form.hasChanges}>
                                {submitting ? 'Enregistrement…' : 'Enregistrer'}
                            </Button>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminCommercialGestureEdit;
